// src/lib/jobs.js
// ───────────────────────────────────────────────────────────────────────────
// 🏎️ Motor de JOBS (lado cliente) — "reutilizar, no duplicar"
// Encapsula la creación de un job para que NewJob, NewJob Lite (y a futuro otros
// puntos de entrada) escriban EXACTAMENTE el mismo documento con la misma
// numeración. Una sola fuente de verdad, varias profundidades de entrada
// (PLAN-MOTOR-UNIFICADO §4).
// ───────────────────────────────────────────────────────────────────────────
import { doc, setDoc, runTransaction, serverTimestamp } from "firebase/firestore";

/**
 * Crea un job nuevo con numeración consecutiva atómica (counters/jobs).
 * Devuelve el id del job (formato "J-<n>"). Lanza si el contador no existe.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} payload  campos del job (clientId y jobName obligatorios)
 * @param {object} user     usuario actual ({ uid })
 * @return {Promise<string>} jobId (ej. "J-1042")
 */
export async function createJob(db, payload, user) {
  if (!payload?.clientId) throw new Error("Falta el cliente (clientId).");
  if (!payload?.jobName?.trim()) throw new Error("Falta el nombre del trabajo.");

  // Numeración consecutiva atómica (mismo mecanismo que NewJobView).
  const counterRef = doc(db, "counters", "jobs");
  const jobNumber = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    if (!snap.exists()) throw new Error("El contador de trabajos (counters/jobs) no existe.");
    const next = (snap.data().lastNumber || 0) + 1;
    tx.update(counterRef, { lastNumber: next });
    return next;
  });
  const jobId = `J-${jobNumber}`;

  // Forma canónica del documento `jobs` (paridad con NewJobView).
  const data = {
    clientId: payload.clientId,
    jobName: payload.jobName.trim(),
    description: payload.description || "",
    deliveryDate: payload.deliveryDate || "",
    priority: payload.priority || "Media",
    quantity: Number(payload.quantity) || 1,
    type: payload.type || "Producto",
    product: "",
    class: "",
    components: [],
    assignedRoles: [],
    documents: [],
    status: { stage: "NUEVO", phase: "PENDIENTE" },
    // Reorder-ready (#2): referencia al SKU del catálogo + snapshot inmutable de specs.
    skuRef: payload.skuRef || null,
    productSnapshot: payload.productSnapshot || null,
    jobNumber,
    createdVia: payload.createdVia || "full", // marca de procedencia (lite/full/solicitud) para adopción/analítica
    // S4a: los trabajos nacidos de una solicitud entran marcados `quick: true`
    // (sin planchas todavía). Solo se escribe cuando es verdad, para no
    // ensuciar los jobs normales con un campo más.
    ...(payload.quick === true ? { quick: true } : {}),
    createdAt: serverTimestamp(),
    createdBy: user?.uid || null,
  };

  await setDoc(doc(db, "jobs", jobId), data);
  return jobId;
}
